'use client'

import { motion } from 'framer-motion'
import { MapPin, Calendar, Clock, Thermometer, Users, Star } from 'lucide-react'
import { ImageWithFallback } from './figma/ImageWithFallback'
import cancunImage from '@/assets/cancun.jpg'

const venueFeatures = [
  {
    icon: Users,
    title: "Capacidad Elite",
    description: "1,500 espectadores",
    color: "from-blue-400 to-cyan-500"
  },
  {
    icon: Star,
    title: "Instalaciones",
    description: "Última tecnología",
    color: "from-yellow-400 to-orange-500"
  },
  {
    icon: Thermometer,
    title: "Clima Perfecto",
    description: "28°C promedio",
    color: "from-green-400 to-teal-500"
  },
  {
    icon: MapPin,
    title: "Ubicación Premium",
    description: "Por revelarse",
    color: "from-purple-400 to-pink-500"
  }
]

const schedule = [
  { date: "4 Dic", event: "Inauguración y Ceremonia", time: "07:00" },
  { date: "5 Dic", event: "Finales Under", time: "16:00 - 20:00" },
  { date: "6 Dic", event: "Categorías libres", time: "08:00" },
  { date: "7 Dic", event: "Final del Campeonato", time: "21:00" }
]

export default function VenueSection() {
  return (
    <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/5 via-transparent to-orange-500/5" />
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={{
            background: [
              'radial-gradient(circle at 20% 20%, rgba(255, 196, 0, 0.1) 0%, transparent 50%)',
              'radial-gradient(circle at 80% 80%, rgba(255, 196, 0, 0.1) 0%, transparent 50%)',
              'radial-gradient(circle at 20% 20%, rgba(255, 196, 0, 0.1) 0%, transparent 50%)'
            ]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 
            className="text-5xl md:text-6xl font-black text-white mb-6"
            style={{ fontWeight: 900 }}
          >
            SEDE <span className="text-yellow-400">CANCÚN</span>
          </motion.h2>
          <div className="h-1 w-32 bg-gradient-to-r from-yellow-400 to-orange-500 mx-auto mb-6" />
          <motion.p 
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            style={{ fontWeight: 400 }}
          >
            El paraíso del Caribe Mexicano será el escenario del evento deportivo más importante del año
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Side - Venue Image */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative rounded-2xl overflow-hidden">
              <ImageWithFallback
                src={cancunImage}
                alt="Estadio Cancún"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              
              {/* Floating Date Badge */}
              <motion.div
                className="absolute top-6 right-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-6 py-3 rounded-full font-black text-lg shadow-2xl"
                animate={{
                  scale: [1, 1.05, 1],
                  rotate: [0, 2, 0, -2, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                4-7 DICIEMBRE
              </motion.div>

              {/* Location Pin */}
              <motion.div
                className="absolute bottom-6 left-6 bg-black/80 backdrop-blur-sm rounded-xl p-4 text-white"
                whileHover={{ scale: 1.05 }}
              >
                <div className="flex items-center space-x-3">
                  <MapPin className="w-6 h-6 text-yellow-400" />
                  <div>
                    <p className="font-bold">Cede por confirmarse</p>
                    <p className="text-sm text-gray-300">Cancún</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Decorative Elements */}
            <motion.div
              className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-full blur-xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>

          {/* Right Side - Information */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Venue Features */}
            <div>
              <h3 className="text-3xl font-black text-white mb-6">
                ¿Por qué <span className="text-yellow-400">Cancún</span>?
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {venueFeatures.map((feature, index) => {
                  const IconComponent = feature.icon
                  
                  return (
                    <motion.div
                      key={feature.title}
                      className="bg-gradient-to-br from-gray-800/50 to-gray-900/80 rounded-xl p-4 backdrop-blur-sm border border-gray-700/50 hover:border-yellow-400/50 transition-all duration-300"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.6 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-lg flex items-center justify-center mb-3`}>
                        <IconComponent className="w-6 h-6 text-black" />
                      </div>
                      <h4 className="text-white font-bold mb-1">{feature.title}</h4>
                      <p className="text-gray-400 text-sm">{feature.description}</p>
                    </motion.div>
                  )
                })}
              </div>
            </div>

            {/* Schedule */}
            <div>
              <h3 className="text-3xl font-black text-white mb-6">
                <Calendar className="inline w-8 h-8 text-yellow-400 mr-3" />
                Cronograma
              </h3>
              
              <div className="space-y-3">
                {schedule.map((item, index) => (
                  <motion.div
                    key={item.date}
                    className="flex items-center justify-between bg-gradient-to-r from-gray-800/50 to-gray-900/80 rounded-xl p-4 backdrop-blur-sm border border-gray-700/50"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    whileHover={{ 
                      scale: 1.02,
                      borderColor: 'rgba(255, 196, 0, 0.5)'
                    }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-3 py-1 rounded-full font-bold text-sm">
                        {item.date}
                      </div>
                      <div>
                        <p className="text-white font-semibold">{item.event}</p>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-400">
                      <Clock className="w-4 h-4 mr-2" />
                      <span className="font-medium">{item.time}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Location Info */}
            <motion.div
              className="bg-gradient-to-br from-yellow-400/10 to-orange-500/10 rounded-xl p-6 border border-yellow-400/20"
              whileHover={{ 
                borderColor: 'rgba(255, 196, 0, 0.4)',
                backgroundColor: 'rgba(255, 196, 0, 0.05)'
              }}
              transition={{ duration: 0.3 }}
            >
              <h4 className="text-xl font-bold text-white mb-4">
                📍 Información de Ubicación
              </h4>
              <div className="space-y-2 text-gray-300">
                <p><span className="text-yellow-400 font-semibold">Dirección:</span> Por revelarse.</p>
                <p><span className="text-yellow-400 font-semibold">Aeropuerto:</span> 25 minutos desde el Aeropuerto Internacional</p>
                <p><span className="text-yellow-400 font-semibold">Hoteles:</span> A menos de 10 minutos de los principales resorts</p>
                <p><span className="text-yellow-400 font-semibold">Transporte:</span> Facilidad de transporte</p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <motion.button
            className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-12 py-4 rounded-full text-xl font-bold hover:shadow-2xl transition-all duration-300 mr-4"
            whileHover={{ 
              scale: 1.05,
              boxShadow: '0 20px 40px rgba(255, 196, 0, 0.4)'
            }}
            whileTap={{ scale: 0.95 }}
            style={{ fontWeight: 700 }}
          >
            RESERVAR HOTEL
          </motion.button>
          
          <motion.button
            className="bg-transparent border-2 border-yellow-400 text-yellow-400 px-12 py-4 rounded-full text-xl font-bold hover:bg-yellow-400 hover:text-black transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ fontWeight: 700 }}
          >
            VER MAPA
          </motion.button>
        </motion.div>
      </div>
    </section>
  )
}